
import { Component, Vue, Mixins } from 'vue-property-decorator';
import { Route } from 'vue-router';
import DateRange from '@/components/DateRange.vue';
import Dialog from '@/components/Dialog.vue';
import api from '@/api';
import itemMixin from '@/minix/itemMixin';
import Swal from 'sweetalert2';
import Util from '@/utils/util';

const productTypeNames = ['產品', '點數', '折價券'];

@Component({ components: { DateRange, Dialog } })
export default class ProductList extends Mixins(Vue.extend(itemMixin)) {
  private data = { data: [], totalCount: 0, pageSize: 20, pageNo: 0 };

  private panel = 0;

  private loading = false;

  merchantCategories: MerchantCategory[] = [];

  merchantList: Merchant[] = [];

  private filter = {
    merchantCategoryId: this.$route.query.merchantCategoryId || undefined,
    merchantId: Util.isNullOrUndefined(this.$route.query.merchantId)
      ? null
      : +this.$route.query.merchantId,
    categoryId: Util.isNullOrUndefined(this.$route.query.categoryId)
      ? null
      : +this.$route.query.categoryId,
    productName: this.$route.query.productName || undefined,
    startStartDate: this.$route.query.startStartDate || undefined,
    endStartDate: this.$route.query.endStartDate || undefined,
    startEndDate: this.$route.query.startEndDate || undefined,
    endEndDate: this.$route.query.endEndDate || undefined,
    status: Util.isNullOrUndefined(this.$route.query.status)
      ? 1
      : +this.$route.query.status,
  };

  private pager = {
    pageSize: Util.isNullOrUndefined(this.$route.query.pageSize)
      ? 20
      : +this.$route.query.pageSize,
    pageNo: Util.isNullOrUndefined(this.$route.query.pageNo)
      ? 1
      : +this.$route.query.pageNo,
  };

  private headers = [
    { text: 'ID', value: 'sequence', sortable: false },
    { text: '供應商', value: 'merchantName', width: 120, sortable: false },
    { text: '點數類別', value: 'categories', width: 120, sortable: false },
    { text: '類型', value: 'productType', width: 100, sortable: false },
    {
      text: '品項名稱',
      value: 'productName',
      width: 120,
      sortable: false,
      class: 'stick-left',
      cellClass: 'stick-left',
    },
    { text: '標籤類別', value: 'tags', width: 120, sortable: false },
    { text: '成本', value: 'cost', width: 100, sortable: false },
    { text: '原價點數', value: 'originalPrice', width: 100, sortable: false },
    {
      text: '回饋百分比',
      value: 'pointBackPercentage',
      width: 120,
      sortable: false,
    },
    {
      text: '最低兌換數量',
      value: 'minimumPurchaseQuantity',
      width: 120,
      sortable: false,
    },
    { text: '單次兌換數量', value: 'tranMulti', width: 120, sortable: false },
    { text: '數量', value: 'quantity', width: 80, sortable: false },
    { text: '票券數量', value: 'voucherQuantity', width: 80, sortable: false },
    { text: '庫存', value: 'inventory', width: 80, sortable: false },
    { text: '上架日期', value: 'startTime', width: 120, sortable: false },
    { text: '下架日期', value: 'endTime', width: 120, sortable: false },
    { text: '啟用', value: 'isEnable', width: 80, sortable: false },
    { text: '修改', value: 'edit', width: 80, sortable: false },
    { text: '序號管理', value: 'manageVouchers', width: 100, sortable: false },
  ];

  private rules = {
    typeRule: [
      (v: any) => !!v.length || '請選擇點數類別',
    ],
  }

  private get totalCount() {
    return this.data.totalCount;
  }

  private get pageCount() {
    return Math.ceil((this.data.totalCount || 0) / (this.data.pageSize || 20));
  }

  private get collection() {
    return [...(this.data.data || [])].map((product: any) => ({
      ...product,
      types: product.categoryClasses.map((cat: any) => cat.catId),
      tags: product.tagClasses.map((tag: any) => tag.tagId),
    }));
  }

  private get categorySelectItems() {
    return [
      { categoryName: '全部', categoryId: '' },
      ...this.merchantCategories,
    ];
  }

  private get merchantSelectItems() {
    const mcId = this.filter.merchantCategoryId;
    return this.merchantList
      .filter(({ categoryId }: any) => !mcId || mcId === categoryId)
      .map((m: any) => ({
        text: m.merchantName,
        value: m.merchantId,
      }));
  }

  private get statusOpts() {
    return [
      {
        text: '未過期',
        value: 1,
      },
      {
        text: '過期',
        value: 2,
      },
      {
        text: '所有',
        value: 3,
      },
    ];
  }

  private dialogInfo = {
    productId: '',
    tags: [],
    types: [],
  };

  private isTypeDialogVisible = false;

  private isTagDialogVisible = false;

  private async updatePage(page: number) {
    await this.query(page);
  }

  private isQueryChanged(query: {
    [key: string]: string | Array<string | null> | null | undefined;
  }): boolean {
    return !Object.keys(query).every(
      (key: string) => query[key]?.toString() === this.$route.query[key],
    );
  }

  private async reset() {
    this.filter = {
      merchantCategoryId: undefined,
      merchantId: null,
      categoryId: null,
      productName: undefined,
      startStartDate: undefined,
      endStartDate: undefined,
      startEndDate: undefined,
      endEndDate: undefined,
      status: 1,
    };
    await this.query(1);
  }

  private async query(page: number) {
    this.pager.pageNo = typeof page === 'number' ? page : 1;
    const query = {
      ...this.filter,
      merchantId: Util.isNullOrUndefined(this.filter.merchantId)
        ? undefined
        : `${this.filter.merchantId}`,
      categoryId: Util.isNullOrUndefined(this.filter.categoryId)
        ? undefined
        : `${this.filter.categoryId}`,
      status: Util.isNullOrUndefined(this.filter.status)
        ? undefined
        : `${this.filter.status}`,
      pageSize: `${this.pager.pageSize}`,
      pageNo: `${this.pager.pageNo}`,
    };
    if (this.isQueryChanged(query)) {
      await this.$router.replace({ query });
    }
    await this.requestData();
  }

  private async requestData() {
    this.loading = true;
    const filter = { ...this.filter, ...this.pager };
    const { status, data } = await api.product.list(filter);
    this.loading = false;
    if (status === 1) {
      this.data = data;
    }
  }

  private getProductTypeName(productType: number) {
    return productTypeNames[Math.log2(productType)];
  }

  private handleToCreatePage() {
    this.$router.push({
      name: 'swapOutProductCreate',
    });
  }

  private handleToEditPage(id: string) {
    this.$router.push({
      name: 'swapOutProductEdit',
      params: { id },
    });
  }

  private handleToVoucherPage(id: string) {
    this.$router.push({
      name: 'swapOutProductVoucherList',
      params: { id },
    });
  }

  private handleOpenTagDialog(data: any) {
    this.dialogInfo = { ...data };
    this.isTagDialogVisible = true;
  }

  private handleOpenTypeDialog(data: any) {
    this.dialogInfo = { ...data };
    this.isTypeDialogVisible = true;
  }

  private handleCloseDialog() {
    this.isTypeDialogVisible = false;
    this.isTagDialogVisible = false;
  }

  private async handleSubmitType() {
    const form = this.$refs.typeForm as any as { validate: () => boolean };
    if (!form.validate()) {
      return;
    }
    const { productId, types } = this.dialogInfo;
    const data = types.map((type) => ({
      catId: type,
    }));
    const { status } = await api.product.setType(productId, data);
    if (status === 1) {
      await this.requestData();
      this.handleCloseDialog();
    }
  }

  private async handleSubmitTag() {
    const { productId, tags } = this.dialogInfo;
    const data = tags.map((type) => ({
      tagId: type,
    }));
    const { status } = await api.product.setTag(productId, data);
    if (status === 1) {
      await this.requestData();
      this.handleCloseDialog();
    }
  }

  private async handleEditEnable({
    productId,
    isEnable,
  }: {
    productId: string;
    isEnable: boolean;
  }) {
    const message = isEnable ? '停用' : '啟用';
    await Swal.fire({
      title: `您確定要${message}嗎?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { status } = await api.product.modifyStatus(productId, !isEnable);
        if (status === 1) {
          await Swal.fire({ icon: 'success', title: `${message}成功` });
          this.query(this.pager.pageNo);
        } else {
          await Swal.fire({
            icon: 'warning',
            title: `${message}失敗，請聯繫系統管理員。`,
          });
        }
      }
    });
  }

  private async beforeRouteEnter(
    to: Route,
    from: Route,
    next: (to?: ((vm: ProductList) => void) | boolean) => void,
  ) {
    await next(async (vm) => {
      await vm.requestData();
    });
  }
}
